import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { setConsent, useConsent, analyticsConfigured } from "@/lib/consent";

const UPDATED = "19 October 2026";

const useCompany = () => {
  const { settings } = useAuth();
  return {
    name: settings.company_name || "[Company legal name]",
    number: settings.company_number || "[Company number]",
    address: settings.company_address || "[Registered office address]",
    email: settings.support_email || "[Contact email]",
    brand: settings.platform_name,
  };
};

const Page = ({ title, testid, children }) => (
  <article data-testid={testid} className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8">
    <p className="caption text-[#0D9488]">Legal</p>
    <h1 className="mt-3 text-4xl font-bold text-[#0B192C] sm:text-5xl">{title}</h1>
    <p className="mt-3 text-sm text-slate-500">Last updated {UPDATED}</p>
    <div className="legal mt-10 space-y-8 text-slate-700">{children}</div>
  </article>
);

const S = ({ h, children }) => (
  <section><h2 className="text-lg font-semibold text-[#0B192C]">{h}</h2><div className="mt-2 space-y-3 text-sm leading-relaxed">{children}</div></section>
);

export const Privacy = () => {
  const c = useCompany();
  return (
    <Page title="Privacy policy" testid="privacy-page">
      <S h="Who we are">
        <p>{c.brand} is operated by {c.name} (company number {c.number}), {c.address}. We are the controller of personal data processed through this website and platform. Contact us at {c.email}.</p>
      </S>
      <S h="What we collect">
        <p>Account details from Google sign-in (name, email, profile picture); business, project and application information you enter; documents you upload; enquiries you send through our contact form; security and activity logs; and, only if you accept analytics cookies, anonymised usage data.</p>
      </S>
      <S h="How we use it and our lawful basis">
        <p>To provide the platform you signed up for (contract); to keep accounts and documents secure and prevent misuse (legitimate interests); to reply to enquiries (legitimate interests); to improve the site through analytics (consent); and to meet legal obligations.</p>
      </S>
      <S h="Your documents and confidential information">
        <p>Uploaded documents are encrypted before storage and are only accessible to members of your workspace with the right permissions. We do not use your confidential invention details to train AI models. Where you ask the assistant for drafting help, the relevant text is sent to our AI provider solely to produce that response.</p>
      </S>
      <S h="Who we share it with">
        <p>Hosting, storage, email delivery, authentication and AI service providers acting on our instructions; advisers you choose to share a project with; and authorities where the law requires. We do not sell personal data.</p>
      </S>
      <S h="International transfers">
        <p>Some providers may process data outside the UK. Where they do, we rely on UK adequacy regulations or the International Data Transfer Agreement or Addendum.</p>
      </S>
      <S h="How long we keep it">
        <p>Account and workspace data is kept while your account is active and deleted or anonymised within [retention period] of closure, unless we must keep it longer by law. Enquiries are kept for up to [retention period].</p>
      </S>
      <S h="Your rights">
        <p>You can ask to access, correct, delete, restrict or port your data, object to processing, and withdraw consent at any time. Email {c.email}. You can also complain to the Information Commissioner's Office at ico.org.uk.</p>
      </S>
      <p className="rounded-xl bg-amber-50 p-4 text-xs text-amber-900" data-testid="legal-review-note">Items in square brackets must be completed, and this policy should be reviewed by a qualified adviser before launch.</p>
    </Page>
  );
};

export const Terms = () => {
  const c = useCompany();
  return (
    <Page title="Terms of use" testid="terms-page">
      <S h="About these terms">
        <p>These terms apply to your use of {c.brand}, provided by {c.name} (company number {c.number}), {c.address}. By creating an account you agree to them.</p>
      </S>
      <S h="What the platform does">
        <p>{c.brand} provides guidance and preparation tools to help you understand IP protection options, find funding and prepare applications. We are not a law firm, patent or trade mark attorney, or government body, and nothing on the platform is legal or financial advice.</p>
      </S>
      <S h="No guaranteed outcomes">
        <p>Preliminary searches are not confirmation of patentability, clearance or freedom to operate. Private dated records are not copyright registration. Funding matches are indicative only. Always confirm eligibility and deadlines with the official source.</p>
      </S>
      <S h="Submissions">
        <p>We only submit on your behalf where a verified, permitted integration exists and you have approved the submission. Otherwise we provide export packs and guided handoff, and you remain responsible for submitting and for the accuracy of what you submit.</p>
      </S>
      <S h="Your account and content">
        <p>Keep your sign-in secure and tell us about unauthorised use. You keep ownership of everything you upload. You give us permission to store and process it only to provide the service.</p>
      </S>
      <S h="Acceptable use">
        <p>Do not upload unlawful content, content you have no right to share, or malware, and do not try to access other workspaces.</p>
      </S>
      <S h="Fees">
        <p>Paid plans and expert reviews are charged as shown on our <Link to="/pricing" className="text-[#1F8A8B] underline">pricing page</Link> at the time of purchase.</p>
      </S>
      <S h="Liability">
        <p>Nothing in these terms limits liability that cannot be limited by law. Otherwise, we are not liable for decisions made by funders or the UK Intellectual Property Office, or for missed deadlines you were responsible for. Our total liability is limited to the fees you paid in the previous 12 months.</p>
      </S>
      <S h="Ending your account and governing law">
        <p>You can close your account at any time. We may suspend accounts that breach these terms. These terms are governed by the laws of England and Wales.</p>
      </S>
      <p className="rounded-xl bg-amber-50 p-4 text-xs text-amber-900" data-testid="legal-review-note">Items in square brackets must be completed, and these terms should be reviewed by a qualified adviser before launch.</p>
    </Page>
  );
};

export const Cookies = () => {
  const consent = useConsent();
  return (
    <Page title="Cookie policy" testid="cookies-page">
      <S h="Essential cookies">
        <p>A secure session cookie keeps you signed in and protects your workspace. These are always on because the platform cannot work without them.</p>
      </S>
      <S h="Analytics cookies">
        <p>Google Analytics cookies (_ga, _ga_*) help us understand which pages are useful. They are only set if you choose "Accept analytics", and IP addresses are anonymised.{!analyticsConfigured() && " Analytics is not currently active on this site."}</p>
      </S>
      <S h="Your choice">
        <p data-testid="cookie-current-choice">Current choice: {consent === "accepted" ? "analytics accepted" : consent === "rejected" ? "essential only" : "not chosen yet"}.</p>
        <button data-testid="cookie-change-button" onClick={() => setConsent(null)} className="btn-ghost btn-sm">Change cookie choice</button>
      </S>
    </Page>
  );
};
